import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Dumbbell, 
  Clock, 
  Flame,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  Play,
  Trash2,
  Zap,
  Target,
  AlertCircle,
  Watch,
  Bell,
  Loader2,
  Info,
  Trophy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Equipment-based workout templates
const equipmentWorkouts = {
  starter: {
    name: 'Starter',
    color: 'bg-green-500',
    description: 'Bodyweight & minimal equipment',
    workouts: [
      {
        name: 'Speed & Agility (No Equipment)',
        duration: 30,
        intensity: 'high',
        exercises: [
          { name: 'High Knees', sets: 3, reps: '30 sec', equipment: 'none' },
          { name: 'Butt Kicks', sets: 3, reps: '30 sec', equipment: 'none' },
          { name: 'Shuttle Runs', sets: 4, reps: '20 yards', equipment: 'cones or markers' },
          { name: 'Lateral Bounds', sets: 3, reps: '10 each side', equipment: 'none' },
          { name: 'Sprint Intervals', sets: 6, reps: '40 yards', equipment: 'none' },
        ]
      },
      {
        name: 'Bodyweight Strength',
        duration: 35,
        intensity: 'moderate',
        exercises: [
          { name: 'Push-Ups', sets: 3, reps: '12-15', equipment: 'none' },
          { name: 'Bodyweight Squats', sets: 3, reps: '15-20', equipment: 'none' },
          { name: 'Lunges', sets: 3, reps: '10 each leg', equipment: 'none' },
          { name: 'Plank', sets: 3, reps: '45 sec', equipment: 'none' },
          { name: 'Burpees', sets: 3, reps: '10', equipment: 'none' },
        ]
      },
      {
        name: 'Resistance Band Circuit',
        duration: 40,
        intensity: 'moderate',
        exercises: [
          { name: 'Band Pull-Aparts', sets: 3, reps: '15', equipment: 'resistance band' },
          { name: 'Band Squats', sets: 3, reps: '12', equipment: 'resistance band' },
          { name: 'Band Rows', sets: 3, reps: '12 each arm', equipment: 'resistance band' },
          { name: 'Band Chest Press', sets: 3, reps: '12', equipment: 'resistance band' },
          { name: 'Band Lateral Walks', sets: 3, reps: '10 each way', equipment: 'resistance band' },
        ]
      }
    ]
  },
  intermediate: {
    name: 'Intermediate',
    color: 'bg-blue-500',
    description: 'Home gym with basic equipment',
    workouts: [
      {
        name: 'Weighted Speed Training',
        duration: 40,
        intensity: 'high',
        exercises: [
          { name: 'Weighted Vest Sprints', sets: 5, reps: '30 yards', equipment: 'weighted vest' },
          { name: 'Medicine Ball Slams', sets: 3, reps: '12', equipment: 'medicine ball' },
          { name: 'Speed Chute Runs', sets: 4, reps: '40 yards', equipment: 'speed parachute' },
          { name: 'Agility Ladder Drills', sets: 4, reps: '2 lengths', equipment: 'agility ladder' },
          { name: 'Box Jumps', sets: 3, reps: '10', equipment: 'plyo box or stairs' },
        ]
      },
      {
        name: 'Kettlebell Power',
        duration: 45,
        intensity: 'high',
        exercises: [
          { name: 'Kettlebell Swings', sets: 4, reps: '15', equipment: 'kettlebell' },
          { name: 'Goblet Squats', sets: 3, reps: '12', equipment: 'kettlebell' },
          { name: 'KB Clean & Press', sets: 3, reps: '8 each arm', equipment: 'kettlebell' },
          { name: 'KB Deadlifts', sets: 3, reps: '12', equipment: 'kettlebell' },
          { name: 'KB Rows', sets: 3, reps: '10 each arm', equipment: 'kettlebell' },
        ]
      },
      {
        name: 'Sled & Vest Conditioning',
        duration: 35,
        intensity: 'high',
        exercises: [
          { name: 'Sled Push', sets: 4, reps: '30 yards', equipment: 'resistance sled' },
          { name: 'Sled Pull', sets: 4, reps: '30 yards', equipment: 'resistance sled' },
          { name: 'Weighted Vest Burpees', sets: 3, reps: '10', equipment: 'weighted vest' },
          { name: 'Weighted Vest Bear Crawl', sets: 3, reps: '20 yards', equipment: 'weighted vest' },
        ]
      }
    ]
  },
  elite: {
    name: 'Elite',
    color: 'bg-purple-500',
    description: 'Full gym access',
    workouts: [
      {
        name: 'Barbell Strength',
        duration: 60,
        intensity: 'high',
        exercises: [
          { name: 'Barbell Back Squat', sets: 4, reps: '6-8', equipment: 'barbell, squat rack' },
          { name: 'Barbell Deadlift', sets: 4, reps: '5', equipment: 'barbell' },
          { name: 'Bench Press', sets: 4, reps: '8', equipment: 'barbell, bench' },
          { name: 'Barbell Rows', sets: 3, reps: '10', equipment: 'barbell' },
          { name: 'Overhead Press', sets: 3, reps: '8', equipment: 'barbell' },
        ]
      },
      {
        name: 'Olympic Lifts',
        duration: 50,
        intensity: 'high',
        exercises: [
          { name: 'Power Clean', sets: 5, reps: '3', equipment: 'barbell, bumper plates' },
          { name: 'Hang Snatch', sets: 4, reps: '3', equipment: 'barbell' },
          { name: 'Clean & Jerk', sets: 4, reps: '2', equipment: 'barbell' },
          { name: 'Front Squats', sets: 3, reps: '8', equipment: 'barbell, squat rack' },
        ]
      },
      {
        name: 'Advanced Plyometrics',
        duration: 45,
        intensity: 'high',
        exercises: [
          { name: 'Depth Jumps', sets: 4, reps: '6', equipment: 'plyo boxes' },
          { name: 'Weighted Box Jumps', sets: 3, reps: '8', equipment: 'vest, plyo box' },
          { name: 'Timed Sprints', sets: 6, reps: '40 yards', equipment: 'timing system' },
          { name: 'Reaction Drills', sets: 4, reps: '30 sec', equipment: 'reaction lights' },
        ]
      }
    ]
  }
};

const WorkoutsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [athleteProfile, setAthleteProfile] = useState(null);
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('action') === 'new');
  const [showAIDialog, setShowAIDialog] = useState(searchParams.get('action') === 'ai');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [newWorkout, setNewWorkout] = useState({
    name: '',
    description: '',
    duration_minutes: 45,
    intensity: 'moderate',
    exercises: []
  });

  useEffect(() => {
    fetchWorkouts();
    fetchAthleteProfile();
  }, []);

  const fetchAthleteProfile = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/users/onboarding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAthleteProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch athlete profile:', error);
    }
  };

  const fetchWorkouts = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/workouts`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setWorkouts(response.data);
    } catch (error) {
      console.error('Failed to fetch workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkout = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/workouts`, newWorkout, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Session complete. You\'re building something.');
      setShowNewDialog(false);
      setNewWorkout({ name: '', description: '', duration_minutes: 45, intensity: 'moderate', exercises: [] });
      fetchWorkouts();
    } catch (error) {
      toast.error('Failed to create workout');
    }
  };

  const handleCompleteWorkout = async (workoutId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/workouts/${workoutId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Session complete! Aura is earned.');
      fetchWorkouts();
    } catch (error) {
      toast.error('Failed to complete workout');
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/workouts/${workoutId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Workout deleted');
      fetchWorkouts();
    } catch (error) {
      toast.error('Failed to delete workout');
    }
  };

  const handleGetAIRecommendation = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/ai/workout-recommendation`, {
        context: 'workout',
        sport: user?.sport,
        user_goals: 'improve performance and build strength'
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAiRecommendation(response.data.recommendation);
    } catch (error) {
      toast.error('Failed to get AI recommendation');
    } finally {
      setAiLoading(false);
    }
  };

  // Training Matrix state
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [completingDrill, setCompletingDrill] = useState(null);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [showWearableModal, setShowWearableModal] = useState(false);
  const [wearableEmail, setWearableEmail] = useState('');

  // Fetch matrix data
  const fetchMatrixData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/workouts/matrix`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatrixData(response.data);
    } catch (error) {
      console.error('Error fetching matrix:', error);
    } finally {
      setMatrixLoading(false);
    }
  };

  // Fetch pending verifications (for supporters)
  const fetchPendingVerifications = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/workouts/matrix/pending`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingVerifications(response.data.pending_verifications || []);
    } catch (error) {
      console.error('Error fetching pending verifications:', error);
    }
  };

  useEffect(() => {
    fetchMatrixData();
    fetchPendingVerifications();
  }, []);

  // Complete a drill
  const handleCompleteDrill = async (drillId) => {
    setCompletingDrill(drillId);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/workouts/matrix/complete`, 
        { drill_id: drillId }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.requires_verification) {
        toast.info(
          <div>
            <p className="font-bold">Pending Verification</p>
            <p className="text-sm">{response.data.message}</p>
          </div>
        );
      } else if (response.data.badge_earned) {
        toast.success(
          <div>
            <p className="font-bold">Badge Earned!</p>
            <p className="text-sm">{response.data.badge_earned} (+25 bonus pts)</p>
          </div>
        );
      } else {
        toast.success(`+${response.data.points_earned || response.data.points} pts - ${response.data.message}`);
      }
      
      // Refresh matrix data
      fetchMatrixData();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to complete drill');
      }
    } finally {
      setCompletingDrill(null);
    }
  };

  // Handle parent verification
  const handleVerifyDrill = async (completionId, action) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/workouts/matrix/verify`, 
        { completion_id: completionId, action }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(action === 'approve' ? 'Drill verified!' : 'Drill rejected');
      fetchPendingVerifications();
    } catch (error) {
      toast.error('Failed to verify drill');
    }
  };

  // Save wearable interest
  const handleWearableInterest = async () => {
    if (!wearableEmail) {
      toast.error('Please enter your email');
      return;
    }
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/workouts/wearable/interest`, 
        { email: wearableEmail }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Thanks! We'll notify you when wearable integration launches!");
      setShowWearableModal(false);
      setWearableEmail('');
    } catch (error) {
      // Just show success anyway - feature is for interest capture
      toast.success("Thanks! We'll notify you when wearable integration launches!");
      setShowWearableModal(false);
    }
  };

  const currentWeek = matrixData?.current_week || Math.min(Math.ceil((new Date().getDate()) / 7), 4);

  // Calculate weekly stats
  const getWeeklyStats = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    const weekWorkouts = workouts.filter(w => {
      const workoutDate = new Date(w.created_at);
      return workoutDate >= startOfWeek;
    });
    
    const completed = weekWorkouts.filter(w => w.completed).length;
    const totalMinutes = weekWorkouts.reduce((sum, w) => sum + (w.duration_minutes || 0), 0);
    
    return {
      total: weekWorkouts.length,
      completed,
      totalMinutes,
      streak: calculateStreak()
    };
  };

  const calculateStreak = () => {
    let streak = 0;
    const sortedWorkouts = [...workouts].filter(w => w.completed).sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
    
    if (sortedWorkouts.length === 0) return 0;
    
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    for (const workout of sortedWorkouts) {
      const workoutDate = new Date(workout.created_at);
      workoutDate.setHours(0, 0, 0, 0);
      
      const diffDays = Math.floor((currentDate - workoutDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 1) {
        streak++;
        currentDate = workoutDate;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const weeklyStats = getWeeklyStats();

  return (
    <DashboardLayout title="Training System">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Section Header */}
        <div className="mb-2">
          <p className="text-aura-text-muted text-sm">Execute your session. Build your performance.</p>
        </div>
        
        {/* Weekly Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-aura-primary">{weeklyStats.total}</p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">This Week</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-aura-success">{weeklyStats.completed}</p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-aura-secondary">{weeklyStats.totalMinutes}</p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Minutes</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-aura-warning">{weeklyStats.streak}</p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Day Streak</p>
            </CardContent>
          </Card>
        </div>

        {/* Equipment-Based Workout Templates */}
        {athleteProfile?.equipment_tier && (
          <Card className="bg-gradient-to-r from-aura-primary/10 to-aura-secondary/10 border-aura-primary/30">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <Target className="w-5 h-5 text-aura-primary" />
                  Workouts For Your Equipment
                </CardTitle>
                <Badge className={`${equipmentWorkouts[athleteProfile.equipment_tier]?.color} text-white border-0`}>
                  {equipmentWorkouts[athleteProfile.equipment_tier]?.name} Level
                </Badge>
              </div>
              <p className="text-sm text-aura-text-muted">
                {equipmentWorkouts[athleteProfile.equipment_tier]?.description}
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-3">
                {equipmentWorkouts[athleteProfile.equipment_tier]?.workouts.map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setNewWorkout({
                        name: template.name,
                        description: template.exercises.map(e => `${e.name}: ${e.sets}x${e.reps}`).join('\n'),
                        duration_minutes: template.duration,
                        intensity: template.intensity,
                        exercises: template.exercises
                      });
                      setShowNewDialog(true);
                    }}
                    className="p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 hover:border-aura-primary/50 transition-all text-left group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Dumbbell className="w-5 h-5 text-aura-primary" />
                      <ChevronRight className="w-4 h-4 text-aura-text-muted group-hover:text-aura-primary transition-colors" />
                    </div>
                    <p className="font-heading font-bold text-white text-sm uppercase tracking-tight">{template.name}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-aura-text-muted">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {template.duration}m
                      </span>
                      <span className="flex items-center gap-1">
                        <Flame className="w-3 h-3" />
                        {template.intensity}
                      </span>
                      <span>{template.exercises.length} exercises</span>
                    </div>
                  </button>
                ))}
              </div>
              {!athleteProfile.onboarding_completed && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  <p className="text-sm text-yellow-300">
                    <a href="/onboarding" className="underline">Complete your profile</a> to get personalized workouts based on your equipment.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="flex-1 sm:flex-none bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12" data-testid="new-workout-btn">
                <Plus className="w-5 h-5 mr-2" />
                Complete Session
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-md mx-4">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  Complete Training Session
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateWorkout} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Workout Name</Label>
                  <Input
                    value={newWorkout.name}
                    onChange={(e) => setNewWorkout({ ...newWorkout, name: e.target.value })}
                    placeholder="e.g., Morning Speed Session"
                    required
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="workout-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Duration (min)</Label>
                    <Input
                      type="number"
                      value={newWorkout.duration_minutes}
                      onChange={(e) => setNewWorkout({ ...newWorkout, duration_minutes: parseInt(e.target.value) })}
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Intensity</Label>
                    <Select value={newWorkout.intensity} onValueChange={(v) => setNewWorkout({ ...newWorkout, intensity: v })}>
                      <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        <SelectItem value="low" className="text-white">Low</SelectItem>
                        <SelectItem value="moderate" className="text-white">Moderate</SelectItem>
                        <SelectItem value="high" className="text-white">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Notes</Label>
                  <Textarea
                    value={newWorkout.description}
                    onChange={(e) => setNewWorkout({ ...newWorkout, description: e.target.value })}
                    placeholder="What did you work on?"
                    className="bg-aura-bg border-white/10 text-white min-h-[100px]"
                  />
                </div>
                <Button type="submit" className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12" data-testid="save-workout-btn">
                  Session Complete
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="flex-1 sm:flex-none border-aura-warning/50 text-aura-warning hover:bg-aura-warning/10 font-heading uppercase tracking-wider h-12"
                data-testid="ai-workout-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                AI Recommendation
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-aura-warning" />
                  AI Workout Plan
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                {!aiRecommendation && !aiLoading && (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-aura-warning mx-auto mb-4" />
                    <p className="text-aura-text-muted mb-4">
                      Get a personalized workout based on your sport, goals, and recovery status
                    </p>
                    <Button 
                      onClick={handleGetAIRecommendation}
                      className="bg-aura-warning hover:bg-aura-warning/90 text-black font-heading uppercase tracking-wider"
                      data-testid="generate-ai-workout-btn"
                    >
                      Generate My Plan
                    </Button>
                  </div>
                )}
                {aiLoading && (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 border-4 border-aura-warning border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-aura-text-muted">Creating your personalized workout...</p>
                  </div>
                )}
                {aiRecommendation && (
                  <div className="space-y-4">
                    <div className="aura-card p-4">
                      <h3 className="font-heading font-bold text-lg text-white mb-2">
                        {aiRecommendation.workout_name || 'Custom Workout'}
                      </h3>
                      <div className="flex gap-4 text-sm text-aura-text-muted">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {aiRecommendation.total_duration || 45} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Flame className="w-4 h-4" />
                          {aiRecommendation.intensity || 'Moderate'}
                        </span>
                      </div>
                    </div>
                    
                    {aiRecommendation.warm_up && (
                      <div>
                        <h4 className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider mb-2">Warm-up</h4>
                        <div className="space-y-2">
                          {aiRecommendation.warm_up.map((item, i) => (
                            <div key={i} className="aura-card p-3 text-sm text-white">
                              {item.exercise} - {item.duration}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiRecommendation.main_workout && (
                      <div>
                        <h4 className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider mb-2">Main Workout</h4>
                        <div className="space-y-2">
                          {aiRecommendation.main_workout.map((item, i) => (
                            <div key={i} className="aura-card p-3 text-sm text-white">
                              <span className="font-medium">{item.exercise}</span>
                              <span className="text-aura-text-muted ml-2">
                                {item.sets}x{item.reps} • {item.rest} rest
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiRecommendation.coach_notes && (
                      <div className="aura-card p-4 bg-aura-primary/10 border-aura-primary/30">
                        <p className="text-sm text-white">{aiRecommendation.coach_notes}</p>
                      </div>
                    )}

                    <Button 
                      onClick={() => {
                        setNewWorkout({
                          name: aiRecommendation.workout_name || 'AI Generated Workout',
                          description: aiRecommendation.coach_notes || '',
                          duration_minutes: aiRecommendation.total_duration || 45,
                          intensity: aiRecommendation.intensity?.toLowerCase() || 'moderate',
                          exercises: []
                        });
                        setShowAIDialog(false);
                        setShowNewDialog(true);
                      }}
                      className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
                    >
                      Use This Workout
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Parent Verification Requests (for supporters) */}
        {pendingVerifications.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                Pending Verifications ({pendingVerifications.length})
              </CardTitle>
              <p className="text-sm text-aura-text-muted">
                Verify your athlete's completed drills
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {pendingVerifications.map((req) => (
                <div 
                  key={req.request_id} 
                  className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10"
                  data-testid={`verification-${req.request_id}`}
                >
                  <div>
                    <p className="text-white font-medium">{req.athlete_name}</p>
                    <p className="text-sm text-aura-text-muted">Completed: {req.drill_name}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleVerifyDrill(req.completion_id, 'approve')}
                      className="bg-aura-success hover:bg-aura-success/90 text-white"
                      data-testid={`approve-${req.completion_id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleVerifyDrill(req.completion_id, 'reject')}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid={`reject-${req.completion_id}`}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* 4-Week Training Matrix - Interactive v2 */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                4-Week Training Matrix
              </CardTitle>
              <div className="flex items-center gap-2">
                {/* Leaderboard Button */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = '/matrix-leaderboard'}
                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                  data-testid="matrix-leaderboard-btn"
                >
                  <Trophy className="w-4 h-4 mr-1" />
                  Leaderboard
                </Button>
                
                {/* Wearable Coming Soon Button */}
                <Dialog open={showWearableModal} onOpenChange={setShowWearableModal}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                      data-testid="wearable-interest-btn"
                    >
                      <Watch className="w-4 h-4 mr-1" />
                      Wearable
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-aura-surface border-white/10 max-w-md mx-4">
                    <DialogHeader>
                      <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider flex items-center gap-2">
                        <Watch className="w-5 h-5 text-cyan-400" />
                        Wearable Integration
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                        <p className="text-cyan-300 font-medium mb-2">Coming Soon!</p>
                        <p className="text-sm text-aura-text-muted">
                          Connect your wearable device (Garmin, Fitbit, Apple Watch, Whoop & more) to automatically verify workouts and track recovery.
                        </p>
                      </div>
                      <div className="space-y-3">
                        <p className="text-sm text-white">Get notified when it launches:</p>
                        <Input
                          type="email"
                          value={wearableEmail}
                          onChange={(e) => setWearableEmail(e.target.value)}
                          placeholder="Enter your email"
                          className="h-12 bg-aura-bg border-white/10 text-white"
                          data-testid="wearable-email-input"
                        />
                        <Button 
                          onClick={handleWearableInterest}
                          className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-heading uppercase tracking-wider"
                          data-testid="wearable-notify-btn"
                        >
                          Notify Me
                        </Button>
                      </div>
                      <div className="text-xs text-aura-text-muted space-y-1">
                        <p className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Auto-verify drill completion</p>
                        <p className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Sync sleep & recovery data</p>
                        <p className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Track heart rate zones</p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {matrixData && (
                  <>
                    <Badge className="bg-aura-primary/20 text-aura-primary">
                      {matrixData.total_points} pts
                    </Badge>
                    {matrixData.pending_count > 0 && (
                      <Badge className="bg-amber-500/20 text-amber-400">
                        {matrixData.pending_count} pending
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>
            {matrixData && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-aura-text-muted mb-1">
                  <span>Weekly Progress ({matrixData.week_start} to {matrixData.week_end})</span>
                  <span>{matrixData.completion_percentage}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-aura-primary to-aura-secondary transition-all duration-500"
                    style={{ width: `${matrixData.completion_percentage}%` }}
                  />
                </div>
                {matrixData.badges && matrixData.badges.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {matrixData.badges.map((badge, i) => (
                      <Badge key={i} className="bg-yellow-500/20 text-yellow-400 text-xs">
                        {badge}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {matrixLoading ? (
              <div className="space-y-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : matrixData ? (
              <div className="space-y-4">
                {matrixData.matrix.map((week) => (
                  <div 
                    key={week.week}
                    className={`rounded-xl border transition-all ${
                      currentWeek === week.week 
                        ? 'border-aura-primary/50 bg-aura-primary/5' 
                        : 'border-white/10 bg-white/5'
                    }`}
                  >
                    {/* Week Header */}
                    <div className="flex items-center justify-between p-3 border-b border-white/5">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-bold text-white">Week {week.week}</span>
                        {currentWeek === week.week && (
                          <Badge className="bg-aura-primary text-white text-xs">Current</Badge>
                        )}
                        {week.week_complete && (
                          <Badge className="bg-aura-success/20 text-aura-success text-xs">Complete!</Badge>
                        )}
                      </div>
                      <span className="text-sm text-aura-text-muted">
                        {week.week_completed_count}/{week.week_target_count} targets met
                      </span>
                    </div>
                    
                    {/* Drills Grid - Enhanced with weekly targets */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3">
                      {week.drills.map((drill) => {
                        const categoryColors = {
                          speed: { border: 'border-aura-primary', text: 'text-aura-primary', bg: 'bg-aura-primary/10', fill: 'bg-aura-primary' },
                          strength: { border: 'border-aura-success', text: 'text-aura-success', bg: 'bg-aura-success/10', fill: 'bg-aura-success' },
                          agility: { border: 'border-aura-secondary', text: 'text-aura-secondary', bg: 'bg-aura-secondary/10', fill: 'bg-aura-secondary' },
                          recovery: { border: 'border-aura-warning', text: 'text-aura-warning', bg: 'bg-aura-warning/10', fill: 'bg-aura-warning' },
                        };
                        const colors = categoryColors[drill.category] || categoryColors.speed;
                        const isLoading = completingDrill === drill.id;
                        
                        return (
                          <div
                            key={drill.id}
                            className={`relative p-3 rounded-lg border-2 transition-all ${
                              drill.is_target_met 
                                ? 'border-aura-success bg-aura-success/10' 
                                : `${colors.border} ${colors.bg}`
                            }`}
                            data-testid={`drill-${drill.id}`}
                          >
                            {/* Status indicators */}
                            <div className="absolute top-2 right-2 flex items-center gap-1">
                              {drill.has_pending && (
                                <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" title="Pending verification" />
                              )}
                              {drill.is_target_met && (
                                <CheckCircle2 className="w-4 h-4 text-aura-success" />
                              )}
                            </div>
                            
                            {/* Category label */}
                            <p className={`text-xs uppercase tracking-wider ${colors.text} opacity-70 mb-1`}>
                              {drill.category}
                            </p>
                            
                            {/* Drill name */}
                            <p className="text-sm font-medium text-white mb-2 pr-6">
                              {drill.name}
                            </p>
                            
                            {/* Weekly progress bar */}
                            <div className="mb-2">
                              <div className="flex items-center justify-between text-xs text-aura-text-muted mb-1">
                                <span>{drill.completed_count}/{drill.weekly_target} this week</span>
                                <span>+{drill.points} pts</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${drill.is_target_met ? 'bg-aura-success' : colors.fill} transition-all duration-300`}
                                  style={{ width: `${drill.progress_percent}%` }}
                                />
                              </div>
                            </div>
                            
                            {/* Complete button */}
                            <Button
                              size="sm"
                              onClick={() => handleCompleteDrill(drill.id)}
                              disabled={isLoading}
                              className={`w-full ${
                                drill.is_target_met 
                                  ? 'bg-white/10 hover:bg-white/20 text-white' 
                                  : 'bg-white/10 hover:bg-white/20 text-white'
                              }`}
                              data-testid={`complete-drill-${drill.id}`}
                            >
                              {isLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Plus className="w-4 h-4 mr-1" />
                                  {drill.is_target_met ? 'Log Extra' : 'Complete'}
                                </>
                              )}
                            </Button>
                            
                            {/* Description tooltip */}
                            <p className="text-xs text-aura-text-muted mt-2 line-clamp-2">
                              {drill.description}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-aura-text-muted py-8">Unable to load training matrix</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Workouts */}
        <div>
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
            Session History
          </h2>
          
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aura-card p-4 animate-pulse">
                  <div className="h-16 bg-white/5 rounded-lg" />
                </div>
              ))}
            </div>
          ) : workouts.length > 0 ? (
            <div className="space-y-3">
              {workouts.map((workout) => (
                <Card key={workout.workout_id} className="bg-aura-surface border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          workout.completed 
                            ? 'bg-aura-success/20' 
                            : 'bg-aura-primary/20'
                        }`}>
                          {workout.completed ? (
                            <CheckCircle2 className="w-5 h-5 text-aura-success" />
                          ) : (
                            <Dumbbell className="w-5 h-5 text-aura-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white truncate">{workout.name}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-aura-text-muted">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {workout.duration_minutes} min
                            </span>
                            <span className="capitalize">{workout.intensity}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!workout.completed && (
                          <Button
                            size="sm"
                            onClick={() => handleCompleteWorkout(workout.workout_id)}
                            className="bg-aura-success hover:bg-aura-success/90 text-white"
                            data-testid={`complete-workout-${workout.workout_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteWorkout(workout.workout_id)}
                          className="text-aura-text-muted hover:text-aura-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <Dumbbell className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">Your Next Session is Ready</h3>
                <p className="text-aura-text-muted mb-4">
                  Execute the plan. Build your performance.
                </p>
                <Button 
                  onClick={() => setShowNewDialog(true)}
                  className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                >
                  Complete First Session
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WorkoutsPage;
